import { RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { PULL_THRESHOLD } from '@/application/pwa/pullToRefresh'
import { classNames } from '@/ui/utils/main'

interface PullToRefreshIndicatorProps {
  /** Current pull travel in px, from `usePullToRefresh`. */
  pullDistance: number
  isRefreshing: boolean
}

/**
 * The spinner revealed by the pull-to-refresh gesture in the installed PWA.
 *
 * Rendered as a fixed overlay rather than in the document flow, so pulling it
 * down never reflows the page beneath. It sits below the sticky navbar's
 * `z-50` so the header stays on top.
 */
export function PullToRefreshIndicator({
  pullDistance,
  isRefreshing,
}: PullToRefreshIndicatorProps) {
  const { t } = useTranslation()

  const isVisible = pullDistance > 0 || isRefreshing
  if (!isVisible) return null

  // While refreshing the spinner parks at the threshold; during the pull it
  // tracks the finger.
  const travel = isRefreshing ? PULL_THRESHOLD : pullDistance
  const hasReachedThreshold = pullDistance >= PULL_THRESHOLD

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={t('common.refreshing')}
      className="fixed inset-x-0 top-0 z-40 flex justify-center pointer-events-none pt-safe-top"
      style={{ transform: `translateY(${travel}px)` }}
    >
      <div
        className={classNames(
          'flex items-center justify-center w-9 h-9 rounded-full bg-card border border-border shadow-md',
          // Past the threshold the icon takes the primary colour, so the
          // commit point is visible before the finger lifts.
          hasReachedThreshold || isRefreshing ? 'text-primary' : 'text-muted-foreground',
        )}
      >
        <RefreshCw
          className={classNames('w-4 h-4', isRefreshing && 'motion-safe:animate-spin')}
          // Before it commits, the icon rotates with the pull to show progress.
          style={
            isRefreshing
              ? undefined
              : { transform: `rotate(${(pullDistance / PULL_THRESHOLD) * 270}deg)` }
          }
        />
      </div>
    </div>
  )
}
